import React, { useState, useEffect } from 'react';
import { X, Check } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';

const BatchAssignModal = ({ selectedIds, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch('/api/tasks/0/assignable-employees')
      .then(r => r.json())
      .then(d => {
        if (d.status === 'success') setEmployees(d.data || []);
      })
      .catch(() => {});
  }, []);

  const handleSubmit = async () => {
    if (!employeeId) return;
    setSubmitting(true);
    setError('');

    try {
      const res = await fetch('/api/tasks/batch-assign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: selectedIds, assignee_id: parseInt(employeeId, 10) }),
      });
      const data = await res.json();
      if (data.status !== 'success') {
        setError(data.message || t('common.request_failed'));
        setSubmitting(false);
        return;
      }
      onSuccess();
    } catch (e) {
      setError(e.message);
      setSubmitting(false);
    }
  };

  const selectedEmp = employees.find(e => e.entity_id === parseInt(employeeId, 10));

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[99999]" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
        onClick={e => e.stopPropagation()}>

        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
            {t('common.batch_assign_responsible')}
          </h2>
          <button onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div className="text-sm text-slate-500 dark:text-slate-400">
            <span dangerouslySetInnerHTML={{ __html: t('common.selected_records_count', { count: selectedIds.length }) }} />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
              {t('common.responsible_employee')}
            </label>
            <select
              value={employeeId}
              onChange={e => setEmployeeId(e.target.value)}
              className="w-full p-2.5 border border-slate-300 dark:border-slate-600 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white"
            >
              <option value="">{t('common.select_employee_placeholder')}</option>
              {employees.map(e => (
                <option key={e.entity_id} value={e.entity_id}>{e.entity_id} - {(e.first_name || '')}{(e.last_name || '')}</option>
              ))}
            </select>
          </div>

          {employeeId && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm">
              <span dangerouslySetInnerHTML={{ __html: t('common.assign_preview', { count: selectedIds.length }) }} />
              <br />
              <span className="text-blue-600 dark:text-blue-400">{selectedEmp ? `${selectedEmp.entity_id} - ${(selectedEmp.first_name || '')}${(selectedEmp.last_name || '')}` : '—'}</span>
            </div>
          )}

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700">
          <button onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors">
            {t('common.cancel')}
          </button>
          <button onClick={handleSubmit}
            disabled={!employeeId || submitting}
            className="flex items-center gap-2 bg-violet-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-violet-700 disabled:opacity-50 transition-all">
            <Check size={16} />
            {submitting ? t('common.assigning') : t('common.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BatchAssignModal;
